import {useCallback, useEffect, useMemo, useRef} from "react";
import {useLocation, useNavigate, useNavigationType} from "react-router";

import {OPEN_CHAT_STATE_KEY} from "../routing/openChatHistory";

/**
 * Слои-страницы поверх экрана, у которых есть запись в истории браузера
 * (issue #1066: «разборы ИИ — страницами, а не всплывающими окнами»).
 *
 * Зачем так, а не отдельным маршрутом. Разбор открывается поверх живого экрана
 * (лента, чат, анкета, мэтч, уведомления). Отдельный маршрут размонтировал бы
 * этот экран — потерялись бы позиция ленты, открытая анкета, мэтч. Поэтому экран
 * остаётся смонтированным, а каждый открытый слой кладёт в историю свою запись
 * с адресом `?analysis=<id>&view=…` на том же пути. Отсюда:
 *   • «назад» браузера и аппаратная «назад» Android (Capacitor уходит назад по
 *     истории WebView) снимают ровно один слой и возвращают на тот же экран;
 *   • кнопка «Назад» на странице — это `history.back()`, а не отдельный путь;
 *   • закрытие слоя кодом отматывает его записи, чтобы «назад» потом не
 *     «проваливался» в пустую запись.
 *
 * Записи помечаются id экземпляра хука. Запись, которой не владеет живой
 * экземпляр (перезагрузка, прямая ссылка, «вперёд»), — «чужая»: её адрес
 * восстанавливает сам экран (`isLiveLayerEntry` ниже, см. useAnalysisFlow).
 */
export const HISTORY_LAYERS_KEY = "rlaLayers";

type LayerMark = {id: string; n: number};
type LayersState = Record<string, LayerMark>;

/** Живые экземпляры хука: id → сколько их слоёв сейчас имеют запись в истории. */
const liveInstances = new Map<string, () => number>();

function layersOf(state: unknown): LayersState {
    if (!state || typeof state !== "object") return {};
    const value = (state as Record<string, unknown>)[HISTORY_LAYERS_KEY];
    return value && typeof value === "object" ? (value as LayersState) : {};
}

/** location.state текущей записи — как его хранит React Router (`history.state.usr`). */
function currentUsr(): Record<string, unknown> {
    if (typeof window === "undefined") return {};
    const usr = (window.history.state as {usr?: unknown} | null)?.usr;
    return usr && typeof usr === "object" ? usr as Record<string, unknown> : {};
}

/**
 * Запись истории принадлежит открытому сейчас слою (её положил живой экземпляр
 * и слой ещё открыт). Иначе — чужая: после перезагрузки, по прямой ссылке,
 * «вперёд» на снятый слой; такую запись экран восстанавливает по адресу.
 */
export function isLiveLayerEntry(state: unknown): boolean {
    return Object.values(layersOf(state)).some((mark) => {
        const synced = liveInstances.get(mark?.id);
        return synced !== undefined && mark.n <= synced();
    });
}

type UseHistoryLayersOptions = {
    /** Пространство имён (несколько независимых наборов слоёв на одном экране). */
    ns: string;
    /** Сколько слоёв открыто сейчас. */
    count: number;
    /** Ключ верхнего слоя: сменился без смены count (покупка → разбор) — адрес переписывается. */
    topKey?: string;
    /** «Назад» снял слои: оставить открытыми только первые `depth`. */
    onTrim: (depth: number) => void;
    /** Параметры адреса для слоя глубины n (1…count); null — адрес не трогать. */
    searchFor?: (n: number) => Record<string, string> | null;
    /** Доп. поля history.state новой записи (например, профиль для восстановления). */
    stateFor?: (n: number) => Record<string, unknown> | null;
};

/** Параметры адреса, которые слои ставят и снимают. Остальные (например, ?name= у «Куда сходить») не трогаем. */
export const ANALYSIS_SEARCH_PARAMS = ["analysis", "view"] as const;

export function useHistoryLayers({ns, count, topKey, onTrim, searchFor, stateFor}: UseHistoryLayersOptions) {
    const location = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const instanceId = useMemo(() => Math.random().toString(36).slice(2, 10), []);
    /** Сколько открытых слоёв уже имеют свою запись в истории. */
    const syncedRef = useRef(0);
    /** Путь, на котором лежат записи слоёв: ушли с него — отматывать нечего. */
    const pathRef = useRef(location.pathname);
    const lastTopKeyRef = useRef(topKey);
    const lastCountRef = useRef(count);
    const countRef = useRef(count);
    countRef.current = count;
    const onTrimRef = useRef(onTrim);
    onTrimRef.current = onTrim;
    const locationRef = useRef(location);
    locationRef.current = location;
    const searchForRef = useRef(searchFor);
    searchForRef.current = searchFor;
    const stateForRef = useRef(stateFor);
    stateForRef.current = stateFor;

    useEffect(() => {
        liveInstances.set(instanceId, () => syncedRef.current);
        return () => {
            liveInstances.delete(instanceId);
        };
    }, [instanceId]);

    const buildTarget = useCallback((n: number) => {
        const loc = locationRef.current;
        const params = new URLSearchParams(loc.search);
        const extra = searchForRef.current?.(n);
        if (extra) {
            for (const key of ANALYSIS_SEARCH_PARAMS) params.delete(key);
            for (const [key, value] of Object.entries(extra)) params.set(key, value);
        }
        const search = params.toString();
        // В новую запись переносим только то, что описывает экран под слоем:
        // открытую переписку (#1132) и метки других наборов слоёв. Одноразовые
        // команды экрана (openNotifications, paymentResume…) не копируем — иначе
        // возврат на запись выполнил бы их второй раз.
        const usr = currentUsr();
        return {
            to: {pathname: loc.pathname, search: search ? `?${search}` : ""},
            state: {
                ...(usr[OPEN_CHAT_STATE_KEY] ? {[OPEN_CHAT_STATE_KEY]: usr[OPEN_CHAT_STATE_KEY]} : {}),
                ...(stateForRef.current?.(n) ?? {}),
                [HISTORY_LAYERS_KEY]: {...layersOf(usr), [ns]: {id: instanceId, n}},
            },
        };
    }, [instanceId, ns]);

    // «Назад»/«вперёд» браузера или Android: подогнать открытые слои под запись.
    useEffect(() => {
        if (navigationType !== "POP") return;
        const mark = layersOf(location.state)[ns];
        const desired = mark && mark.id === instanceId ? mark.n : 0;
        if (desired >= syncedRef.current) return;
        syncedRef.current = desired;
        if (countRef.current > desired) onTrimRef.current(desired);
        // location.key — единственный надёжный признак новой записи истории.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Открыли слой — кладём запись; закрыли кодом — отматываем свои записи.
    useEffect(() => {
        const synced = syncedRef.current;
        if (count > synced) {
            if (synced === 0) pathRef.current = locationRef.current.pathname;
            for (let n = synced + 1; n <= count; n += 1) {
                const {to, state} = buildTarget(n);
                navigate(to, {state});
            }
            syncedRef.current = count;
        } else if (count < synced) {
            syncedRef.current = count;
            // Слой закрыт кодом вместе с уходом на другой экран — записи уже позади,
            // отматывать их значило бы отменить этот уход.
            if (typeof window !== "undefined" && window.location.pathname === pathRef.current) {
                navigate(count - synced);
            }
        } else if (count > 0 && count === lastCountRef.current && topKey !== lastTopKeyRef.current) {
            // Тот же слой сменил содержимое (покупка → разбор): переписываем адрес записи.
            const {to, state} = buildTarget(count);
            navigate(to, {state, replace: true});
        }
        lastTopKeyRef.current = topKey;
        lastCountRef.current = count;
    }, [buildTarget, count, navigate, topKey]);

    /**
     * Закрыть верхний слой так, как это сделала бы «назад»: если у него есть
     * запись — шаг назад по истории (слой снимет POP-обработчик выше), иначе —
     * обычное закрытие.
     */
    const back = useCallback((fallback: () => void) => {
        if (syncedRef.current > 0 && syncedRef.current === countRef.current) {
            navigate(-1);
            return;
        }
        fallback();
    }, [navigate]);

    return {back};
}

/**
 * Уйти со всех открытых слоёв-страниц (шаг назад на их общую глубину) и затем
 * выполнить действие. Нужно шапке приложения на странице разбора: фильтр,
 * корона, колокольчик открываются так же, как на основном экране, — поверх
 * экрана-источника, а «назад» из них не возвращает на снятую страницу.
 */
export function leaveLayersThen(action: () => void): void {
    if (typeof window === "undefined") {
        action();
        return;
    }
    let depth = 0;
    for (const mark of Object.values(layersOf(currentUsr()))) {
        const synced = liveInstances.get(mark?.id);
        if (synced !== undefined) depth += Math.min(mark.n, synced());
    }
    if (depth <= 0) {
        action();
        return;
    }
    const onPop = () => {
        window.removeEventListener("popstate", onPop);
        // Даём роутеру отрисовать запись-источник и снять слои, потом — действие.
        window.setTimeout(action, 60);
    };
    window.addEventListener("popstate", onPop);
    window.history.go(-depth);
}

/** Параметры адреса открытой страницы разбора (для восстановления после перезагрузки/прямой ссылки). */
export function readAnalysisSearch(search: string): {peerId: string; view: string | null} | null {
    const params = new URLSearchParams(search);
    const peerId = params.get("analysis");
    if (!peerId) return null;
    return {peerId, view: params.get("view")};
}

/** Тот же адрес без параметров разбора. */
export function stripAnalysisSearch(search: string): string {
    const params = new URLSearchParams(search);
    for (const key of ANALYSIS_SEARCH_PARAMS) params.delete(key);
    const rest = params.toString();
    return rest ? `?${rest}` : "";
}

/**
 * Адрес открытой страницы разбора, которую должен поднять этот экран: есть
 * `?analysis=` и запись не принадлежит открытому слою (перезагрузка, прямая
 * ссылка, «вперёд»). Иначе null.
 */
export function pendingAnalysisRestore(search: string, state: unknown): {peerId: string; view: string | null} | null {
    const target = readAnalysisSearch(search);
    if (!target) return null;
    return isLiveLayerEntry(state) ? null : target;
}
